/**
 * Pure transforms for matrix device CRUD.
 *
 * No I/O, no signals, no localStorage. The Phase 2 store layer wraps
 * each of these in a `setMatrixLibrary(...)` call so it persists; tests
 * import this module directly to exercise the math without booting a
 * browser store.
 *
 * Convention: every function takes the full MatrixLibrary as the first
 * arg and returns a new MatrixLibrary (immutable). Mutations are at the
 * structural-share level — re-render cost in Solid is bounded by which
 * arrays actually changed identity.
 */

import type {
  MatrixDevice,
  MatrixDeviceColor,
  MatrixLibrary,
} from '@opbuttons/schema';

/** Editor-only palette — exported because both the store + the
 *  DeviceEditor swatch row need it. Order matches the swatch order in
 *  the UI. */
export const MATRIX_DEVICE_PALETTE: MatrixDeviceColor[] = [
  'sky', 'orchid', 'amber', 'lime', 'rose', 'forest', 'cyan', 'magenta',
];

/** Pick the next device color so freshly-added devices visually cluster
 *  away from existing ones. Cycles through the palette — once every hue
 *  is in use, repeats from the start by index. */
export function nextDeviceColor(library: MatrixLibrary): MatrixDeviceColor {
  const used = new Set(library.devices.map((d) => d.color));
  for (const c of MATRIX_DEVICE_PALETTE) if (!used.has(c)) return c;
  return MATRIX_DEVICE_PALETTE[library.devices.length % MATRIX_DEVICE_PALETTE.length] ?? 'cyan';
}

export interface AddDeviceInput {
  /** Required — caller supplies (typically a ulid). Kept as a param so
   *  the math stays deterministic and testable. */
  id: string;
  name?: string;
  color?: MatrixDeviceColor;
  notes?: string;
}

/** Add a device to the library. Returns the new library + the created
 *  device record (so the caller can immediately select it in the UI). */
export function addDeviceTo(
  library: MatrixLibrary,
  input: AddDeviceInput,
): { library: MatrixLibrary; device: MatrixDevice } {
  const device: MatrixDevice = {
    id: input.id,
    name: input.name?.trim() || 'New device',
    color: input.color ?? nextDeviceColor(library),
    ...(input.notes ? { notes: input.notes } : {}),
  };
  return {
    library: { ...library, devices: [...library.devices, device] },
    device,
  };
}

/** Patch a device. Unspecified fields stay as-is. Empty `name` falls
 *  back to the existing name (the UI never wants a blank). Empty
 *  `notes` strips the optional field entirely so empties don't
 *  round-trip through JSON as `notes: ""`. */
export function updateDeviceIn(
  library: MatrixLibrary,
  id: string,
  patch: Partial<Pick<MatrixDevice, 'name' | 'color' | 'notes'>>,
): MatrixLibrary {
  const devices = library.devices.map((d) => {
    if (d.id !== id) return d;
    const next: MatrixDevice = {
      ...d,
      ...(patch.name !== undefined ? { name: patch.name.trim() || d.name } : {}),
      ...(patch.color !== undefined ? { color: patch.color } : {}),
    };
    if (patch.notes !== undefined) {
      if (patch.notes.trim() === '') {
        delete (next as Partial<MatrixDevice>).notes;
      } else {
        next.notes = patch.notes;
      }
    }
    return next;
  });
  return { ...library, devices };
}

/** Remove a device. Also strips any claims pointing at it; slots that
 *  end up with zero claims after the strip are dropped entirely (an
 *  un-claimed slot has no reason to exist). */
export function removeDeviceFrom(library: MatrixLibrary, id: string): MatrixLibrary {
  const slots = library.slots
    .map((s) => ({ ...s, claims: s.claims.filter((c) => c.deviceId !== id) }))
    .filter((s) => s.claims.length > 0);
  const devices = library.devices.filter((d) => d.id !== id);
  return { ...library, devices, slots };
}

/** Reorder a device in the devices[] array. Out-of-range targets clamp
 *  to [0, length-1]. No-op when source equals target. */
export function moveDeviceIn(library: MatrixLibrary, id: string, toIndex: number): MatrixLibrary {
  const from = library.devices.findIndex((d) => d.id === id);
  if (from === -1) return library;
  const clampedTo = Math.max(0, Math.min(library.devices.length - 1, toIndex));
  if (from === clampedTo) return library;
  const devices = library.devices.slice();
  const [picked] = devices.splice(from, 1);
  if (!picked) return library;
  devices.splice(clampedTo, 0, picked);
  return { ...library, devices };
}

/** Count how many claims reference a given device id. */
export function countClaimsForDevice(library: MatrixLibrary, id: string): number {
  return library.slots.reduce(
    (sum, s) => sum + s.claims.filter((c) => c.deviceId === id).length,
    0,
  );
}
